import { useSelector } from 'react-redux';
import { format as formatDate } from 'date-fns';
import { de } from 'date-fns/locale';
import { SubscriptionDetailsResponse } from '@kimuno/api-sdk/lib/types/responses/Billing';

import { dateTimeFormat, subscriptionDateFormat } from '../constants';
import { getStripeSubscriptionDetails, getSubscription, selectUser } from '../reducers/user.selectors';
import { currencyFormat } from '../utils/format';
import { isWithinWithdrawalWindow } from './withdrawalEligibility';
import { computeTermEndDate, resolveTermAnchor } from './subscriptionTerm';

export enum SubscriptionStatus {
  Active = 'Active',
  Cancelled = 'Cancelled',
  PauseRequested = 'Pause requested',
  Paused = 'Paused',
  Blocked = 'Blocked',
  WithdrawalRequested = 'WithdrawalRequested',
}

// Map the synced Stripe subscription details to the shared SubscriptionStatus
// the label chip + notification alert render off. Withdrawal wins over pause
// (mirrors the legacy path), and both win over the plain status ladder.
//
// SM-411 wires the pause states: a `pause` block on the response is only ever
// present for non-terminal rows (server filters `resumed`/`rejected` out), so
// its presence means "in-flight pause request". Within that:
//   - `active` = Stripe is actively paused (pause window running) → Paused
//   - `pending` / `approved` / `pause_failed` = admin flow in progress or
//     Stripe write not landed yet → PauseRequested (customer-visible: "your
//     pause is being processed")
const mapStripeSubscriptionStatus = (
  details: SubscriptionDetailsResponse | undefined,
): SubscriptionStatus => {
  const subscription = details?.subscription;
  if (!subscription) return SubscriptionStatus.Active;

  // An in-flight withdrawal (Widerruf) takes precedence, mirroring the legacy path.
  if (details?.revocation?.request) return SubscriptionStatus.WithdrawalRequested;
  // SM-433: either signal is authoritative — an admin/webhook race can leave
  // the pause row stale while `subscriptions.status` reflects the true state.
  if (subscription.status === 'paused' || details?.pause?.status === 'active') {
    return SubscriptionStatus.Paused;
  }
  // SM-411: pause request in flight — the customer's request exists but the
  // sub isn't yet paused (row at pending/approved/pause_failed, `subscription.status`
  // still active). Renders "Pause requested" until the webhook completes.
  if (details?.pause) {
    return SubscriptionStatus.PauseRequested;
  }
  if (subscription.isCanceled || subscription.status === 'canceled' || subscription.cancelAt) {
    return SubscriptionStatus.Cancelled;
  }
  if (subscription.status === 'past_due' || subscription.status === 'unpaid') {
    return SubscriptionStatus.Blocked;
  }
  return SubscriptionStatus.Active;
};

export const useSubscriptionInfoData = () => {
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const stripeSubscriptionDetails = useSelector(getStripeSubscriptionDetails);

  // user type 1 B2C and B2B without subscription
  const isBasicUserWithoutSubscription = !!user?.isBasisUser && !subscription;
  // user type 2,3,4 B2B
  const isBusinessUser = !!user?.isSchoolUser || !!user?.isLibraryUser || !!user?.isKeycloakSSO;
  // user type 1,2,3,4 B2C with subscription
  const isB2CUserWithSubscription = !isBasicUserWithoutSubscription && !isBusinessUser;
  const pauseRequested = user?.pauseExperimentStatus === 'requested';
  const withdrawalRequested = user?.withdrawalRequestStatus === 'requested';
  let withdrawalSubmittedAt = user?.withdrawalRequestedAt
    ? formatDate(new Date(user.withdrawalRequestedAt), dateTimeFormat, { locale: de })
    : undefined;
  const canUpgrade = !!user?.canUpgrade;
  const allowAutomaticCancellation = !!user?.allowAutomaticCancellation;
  const isLegacyTrial = !!subscription?.trial?.isInTrial;
  // SM-426: Stripe users have no legacy Zuora subscription, so their trial state
  // lives on the synced Stripe details rather than `subscription.trial`.
  const isStripeTrial = stripeSubscriptionDetails?.subscription?.status === 'trialing';
  const isInTrial = isLegacyTrial || isStripeTrial;
  const withinWithdrawalWindow = isWithinWithdrawalWindow(subscription);

  const lastProduct = subscription?.products
    .filter(product => !product.plan?.name.includes('PromoCode'))
    .at(-1);

  let productName = lastProduct ? `${lastProduct.name}, ${lastProduct.plan.name}`: '';
  let price = currencyFormat(subscription?.licence?.pricePerMonth || 0);
  let reducedPrice = price;
  let discountMonths = 0;

  let rawStartDate = subscription?.startDate;
  let rawEndDate = subscription?.endDate;

  let priceText = 'price per month';
  let discountText = '';
  let pauseStartDate, pauseEndDate;

  let subscriptionStatus = subscription?.status as SubscriptionStatus || SubscriptionStatus.Active;

  if (isB2CUserWithSubscription) {
    const isCancelledSubscription = subscription?.status === SubscriptionStatus.Cancelled;
    const isSuspendedSubscription = subscription?.status === 'Suspended';
    const isScheduledToCancel = subscription?.followingSubscription.isScheduledToCancel;
    const hasPromoCode = !!subscription?.promoCode && !!subscription?.discounts;

    if (isSuspendedSubscription) {
      rawEndDate = subscription?.followingSubscription?.endDate;
    }

    if (isLegacyTrial) {
      rawEndDate = subscription.trial?.endDate;
      rawStartDate = subscription.trial?.startDate;
      priceText = isCancelledSubscription ? '' : 'price after trial';
    }

    if (isScheduledToCancel) {
      rawEndDate = subscription.followingSubscription.endDate;
    }

    if (hasPromoCode) {
      const firstDiscount = subscription.discounts[0];
      if (firstDiscount?.discountPercentage) {
        discountText = `${firstDiscount.discountPercentage}%`;
      } else if (firstDiscount?.discountAmount) {
        discountText = currencyFormat(firstDiscount.discountAmount);
      }
      if (firstDiscount?.isFixedPeriod) {
        discountMonths = firstDiscount.upToPeriods;
        priceText = isInTrial
          ? isCancelledSubscription
            ? ''
            : `${priceText} after discount`
          : 'price after discount';
        reducedPrice = currencyFormat(subscription.reducedPrice);
      } else {
        price = currencyFormat(subscription.reducedPrice);
      }
    }

    if (pauseRequested) {
      subscriptionStatus = SubscriptionStatus.PauseRequested;
    }

    // Withdrawal takes precedence over pause so the chip always reflects the most recent user action.
    if (withdrawalRequested) {
      subscriptionStatus = SubscriptionStatus.WithdrawalRequested;
    }

    // Pause approved but not in effect yet
    if (subscription?.suspension && subscription.suspension.isPause) {
      pauseStartDate =
        formatDate(new Date(subscription.suspension.startDate), subscriptionDateFormat, { locale: de });
      pauseEndDate = formatDate(new Date(subscription.suspension.endDate), subscriptionDateFormat, { locale: de });
    }

    // Pause in effect
    if (isSuspendedSubscription && subscription.suspension?.isPause) {
      productName += ' (pausiert)';
      subscriptionStatus = SubscriptionStatus.Paused;
    }

    // Suspension in effect
    if (isSuspendedSubscription && !subscription.suspension?.isPause) {
      subscriptionStatus = SubscriptionStatus.Blocked;
    }

  }

  // SM-383: a fully terminated Stripe subscription (`status === 'canceled'`)
  // means the user has already been downgraded to Basis — no remaining access.
  // Distinct from a scheduled term-end cancel (`status === 'active'` + `cancelAt`),
  // which keeps access until `cancelAt`. Callers route terminated subs to the
  // basic-user view instead of the misleading "verfügbar bis {periodEnd}" banner
  // (periodEnd is not shortened on an immediate Stripe cancel). An in-flight
  // revocation still takes precedence, mirroring mapStripeSubscriptionStatus.
  const isStripeSubscriptionTerminated =
    !!user?.isStripeUser &&
    stripeSubscriptionDetails?.subscription?.status === 'canceled' &&
    !stripeSubscriptionDetails?.revocation?.request;

  // SM-191: Stripe users have no legacy Zuora subscription, so drive the status
  // chip, the info/warning alert and the end date off the synced Stripe details.
  if (user?.isStripeUser) {
    subscriptionStatus = mapStripeSubscriptionStatus(stripeSubscriptionDetails);

    // SM-411: derive the pause window from the `pause` block. `pauseStartsAt`
    // + `pauseEndsAt` are set on admin approval, so a `pending` row leaves
    // both undefined — SubscriptionStatusAlert already handles that.
    const stripePause = stripeSubscriptionDetails?.pause;
    if (stripePause?.pauseStartsAt) {
      pauseStartDate = formatDate(
        new Date(stripePause.pauseStartsAt),
        subscriptionDateFormat,
        { locale: de },
      );
    }
    if (stripePause?.pauseEndsAt) {
      pauseEndDate = formatDate(
        new Date(stripePause.pauseEndsAt),
        subscriptionDateFormat,
        { locale: de },
      );
    }
    // Mirror the legacy Zuora "productName + (pausiert)" suffix so the
    // Mein Konto product line reads the same in both cohorts.
    if (subscriptionStatus === SubscriptionStatus.Paused) {
      productName = productName ? `${productName} (pausiert)` : '(pausiert)';
    }

    const stripeSubscription = stripeSubscriptionDetails?.subscription;
    rawStartDate = stripeSubscription?.periodStart ?? rawStartDate;
    // SM-386: "Nächstes Verlängerungsdatum" is the fixed 12-month committed term
    // end, not Stripe's `current_period_end` (a 1-month date on a monthly-billed
    // sub, which read as the subscription ending next month). A scheduled
    // term-end cancellation (`cancelAt`) still wins; otherwise use the computed
    // term end (anchor + 12mo), falling back to `periodEnd` when no anchor is
    // available. Anchor precedence mirrors `computeCommitmentEnd` in the email.
    const termAnchor = resolveTermAnchor(stripeSubscription);
    const termEnd = termAnchor ? computeTermEndDate(termAnchor).toISOString() : undefined;
    // SM-426: while the sub is still trialing the label reads "Probezeitraum
    // endet am", so show the trial end itself — NOT the committed term end
    // (anchor + 12mo), which is the post-conversion "Nächstes Verlängerungsdatum".
    // A scheduled cancel (`cancelAt`) still wins, which is why the date corrects
    // once the trial is cancelled.
    const trialEnd =
      stripeSubscription?.status === 'trialing' ? stripeSubscription?.trialEndAt ?? undefined : undefined;
    rawEndDate =
      stripeSubscription?.cancelAt ?? trialEnd ?? termEnd ?? stripeSubscription?.periodEnd ?? rawEndDate;

    const revocationSubmittedAt = stripeSubscriptionDetails?.revocation?.request?.submittedAt;
    if (revocationSubmittedAt) {
      withdrawalSubmittedAt = formatDate(new Date(revocationSubmittedAt), dateTimeFormat, { locale: de });
    }
  }

  const startDate = formatDate(new Date(rawStartDate || Date.now()), subscriptionDateFormat, { locale: de });
  const endDate = formatDate(new Date(rawEndDate || Date.now()), subscriptionDateFormat, { locale: de });

  return {
    user,
    subscription,
    isBasicUserWithoutSubscription,
    isBusinessUser,
    isB2CUserWithSubscription,
    price,
    discountMonths,
    reducedPrice,
    productName,
    startDate,
    endDate,
    subscriptionStatus,
    isStripeSubscriptionTerminated,
    canUpgrade,
    allowAutomaticCancellation,
    priceText,
    discountText,
    pauseStartDate,
    pauseEndDate,
    isInTrial,
    withdrawalSubmittedAt,
    isWithinWithdrawalWindow: withinWithdrawalWindow,
  };
};

export default useSubscriptionInfoData;
